import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { XStack, YStack } from "tamagui";
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Compass,
  FileText,
  Heart,
  Info,
  LifeBuoy,
  PencilLine,
  Settings as SettingsIcon,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Users,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getRecentMemberActivityCutoffIso } from "../../shared/activities";
import {
  KINDRED_DATA_RIGHTS_MENU_SUBTITLE,
  KINDRED_PRIVACY_DATA_RIGHTS_LABEL,
  SUPPORT_KINDRED_PAGE,
  SUPPORT_KINDRED_ROUTE,
} from "../../shared/support";
import { getContentHealthSummary } from "../../shared/system";
import {
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppLoadingState,
  AppSection,
  AppSectionHeader,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

interface Profile {
  display_name: string | null;
  postcode: string | null;
  avatar_url: string | null;
  created_at: string;
}

const formatCountNoun = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

const MyKindred = () => {
  const { isPrivilegedUser, user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectionCount, setConnectionCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminAttentionCount, setAdminAttentionCount] = useState(0);
  const [policiesOpen, setPoliciesOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const [{ data }, { data: conns }, { data: roles }] = await Promise.all([
      supabase
        .from("my_private_profile")
        .select("display_name, postcode, avatar_url, created_at")
        .single(),
      supabase
        .from("connections")
        .select("id")
        .eq("status", "connected")
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`),
      supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .in("role", ["admin", "moderator"]),
    ]);

    if (data) {
      setProfile(data as Profile);
    }

    setConnectionCount(conns?.length ?? 0);

    const roleNames = new Set((roles ?? []).map((entry) => entry.role));
    const nextIsAdmin = roleNames.has("admin");
    setIsAdmin(nextIsAdmin);

    if (nextIsAdmin) {
      const recentActivityCutoff = getRecentMemberActivityCutoffIso();
      const [
        { count: openDeletionRequests },
        { count: openLoginSupportRequests },
        { count: pendingReports },
        { count: memberEventDrafts },
        { count: recentMemberActivities },
        { data: contentCards },
      ] = await Promise.all([
        supabase
          .from("account_deletion_requests")
          .select("id", { count: "exact", head: true })
          .in("status", ["pending", "in_review"]),
        supabase
          .from("auth_support_requests")
          .select("id", { count: "exact", head: true })
          .in("status", ["open", "in_progress"]),
        supabase.from("reports").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase
          .from("events")
          .select("id", { count: "exact", head: true })
          .eq("is_published", false),
        supabase
          .from("adventures")
          .select("id", { count: "exact", head: true })
          .eq("status", "active")
          .gte("created_at", recentActivityCutoff),
        supabase.from("content_cards").select("id, title, section, category, last_reviewed_at, is_active"),
      ]);

      const contentHealth = getContentHealthSummary(contentCards ?? []);
      setAdminAttentionCount(
        (openDeletionRequests ?? 0) +
        (openLoginSupportRequests ?? 0) +
        (pendingReports ?? 0) +
        (memberEventDrafts ?? 0) +
        (recentMemberActivities ?? 0) +
        contentHealth.dueNowCount,
      );
    } else {
      setAdminAttentionCount(0);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!user) return;

    const handleFocus = () => {
      void fetchAll();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void fetchAll();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchAll, user]);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`web-my-kindred-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "connections" }, () => {
        void fetchAll();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchAll, user]);

  if (loading) {
    return (
      <AppLoadingState
        title="Loading your menu"
        body="Getting your profile and account options ready."
      />
    );
  }

  const memberSince = profile?.created_at ? format(new Date(profile.created_at), "MMMM yyyy") : "";

  return (
    <AppSection pt="$sm" gap="$md">
      <button type="button" onClick={() => navigate("/settings")} className="block w-full text-left">
        <AppCard>
          <AppCardBody py="$sm">
            <XStack alignItems="center" gap="$md">
              <Avatar className="h-14 w-14 shrink-0">
                {profile?.avatar_url ? <AvatarImage src={profile.avatar_url} alt="" /> : null}
                <AvatarFallback className="bg-primary/20 text-lg font-semibold text-primary">
                  {(profile?.display_name || "?")[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <YStack flex={1} minWidth={0} gap={3}>
                <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$md" lineHeight="$md">
                  {profile?.display_name || "Kindred member"}
                </AppText>
                <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                  {[profile?.postcode, memberSince ? `Member since ${memberSince}` : null].filter(Boolean).join(" · ")}
                </AppText>
              </YStack>
              <PencilLine size={17} className="shrink-0 text-primary" />
            </XStack>
          </AppCardBody>
        </AppCard>
      </button>

      {isAdmin ? (
        <MenuGroup title="Manage Kindred">
          <MenuLinkRow
            icon={<Shield size={18} className="text-primary" />}
            title="Admin"
            subtitle={
              adminAttentionCount > 0
                ? `${formatCountNoun(adminAttentionCount, "item needs", "items need")} your attention`
                : "Manage members, activities and content"
            }
            badge={adminAttentionCount > 0 ? String(adminAttentionCount) : undefined}
            onPress={() => navigate("/admin")}
          />
        </MenuGroup>
      ) : null}

      <MenuGroup title="Your Kindred">
        <MenuLinkRow
          icon={<Compass size={18} className="text-primary" />}
          title="My adventures"
          subtitle="Activities you organise or join"
          onPress={() => navigate("/my-adventures")}
        />
        <MenuLinkRow
          icon={<Users size={18} className="text-primary" />}
          title="Connections"
          subtitle={
            connectionCount > 0
              ? formatCountNoun(connectionCount, "connection", "connections")
              : "Find and manage your connections"
          }
          onPress={() => navigate("/community/members")}
        />
        <MenuLinkRow
          icon={<BookOpen size={18} className="text-primary" />}
          title="Saved support"
          subtitle="Support packs you want to keep close"
          onPress={() => navigate("/support")}
        />
      </MenuGroup>

      <MenuGroup title="Help & account">
        <MenuLinkRow
          icon={<SettingsIcon size={18} className="text-primary" />}
          title="Settings"
          subtitle="Profile, notifications and account"
          onPress={() => navigate("/settings")}
        />
        <MenuLinkRow
          icon={<Heart size={18} className="text-primary" />}
          title={SUPPORT_KINDRED_PAGE.menuTitle}
          subtitle="Help keep Kindred free for everyone"
          onPress={() => navigate(SUPPORT_KINDRED_ROUTE)}
        />
        <MenuLinkRow
          icon={<LifeBuoy size={18} className="text-primary" />}
          title="Contact & complaints"
          subtitle="Support, safeguarding and formal concerns"
          onPress={() => navigate("/contact-complaints")}
        />
        <MenuLinkRow
          icon={<Info size={18} className="text-primary" />}
          title="About Kindred"
          subtitle="About, policies and social links"
          onPress={() => navigate("/about-kindred")}
        />
      </MenuGroup>

      <AppCard>
        <AppCardBody p={0}>
          <MenuLinkRow
            icon={<ShieldCheck size={18} className="text-primary" />}
            title="Policies & safety"
            subtitle="Privacy, terms, conduct and safeguarding"
            expanded={policiesOpen}
            onPress={() => setPoliciesOpen((current) => !current)}
          />
          {policiesOpen ? (
            <div className="border-t border-border px-3 pb-1">
              <MenuLinkRow
                icon={<BookOpen size={18} className="text-primary" />}
                title="How Kindred guidance works"
                subtitle="How support packs are written and reviewed"
                onPress={() => navigate("/editorial-policy")}
              />
              <MenuLinkRow
                icon={<Shield size={18} className="text-primary" />}
                title="Privacy Policy"
                subtitle="How Kindred handles your data"
                onPress={() => navigate("/privacy")}
              />
              <MenuLinkRow
                icon={<ShieldAlert size={18} className="text-primary" />}
                title={KINDRED_PRIVACY_DATA_RIGHTS_LABEL}
                subtitle={KINDRED_DATA_RIGHTS_MENU_SUBTITLE}
                onPress={() => navigate("/data-rights")}
              />
              <MenuLinkRow
                icon={<FileText size={18} className="text-primary" />}
                title="Terms & Conditions"
                subtitle="The rules for using Kindred"
                onPress={() => navigate("/terms")}
              />
              <MenuLinkRow
                icon={<ShieldCheck size={18} className="text-primary" />}
                title="Code of Conduct"
                subtitle="The rules that keep Kindred safe"
                onPress={() => navigate("/conduct")}
              />
              <MenuLinkRow
                icon={<Shield size={18} className="text-primary" />}
                title="Child Safety Standards"
                subtitle="Safeguarding and child-safety risks"
                onPress={() => navigate("/child-safety")}
              />
              {isPrivilegedUser ? (
                <MenuLinkRow
                  icon={<Shield size={18} className="text-primary" />}
                  title="Moderation guidance"
                  subtitle="Internal guidance for moderators"
                  onPress={() => navigate("/moderation-guidelines")}
                />
              ) : null}
            </div>
          ) : null}
        </AppCardBody>
      </AppCard>
    </AppSection>
  );
};

const MenuGroup = ({ children, title }: { children: ReactNode; title: string }) => (
  <AppCard>
    <AppCardHeader>
      <AppSectionHeader title={title} />
    </AppCardHeader>
    <AppCardBody p={0}>{children}</AppCardBody>
  </AppCard>
);

const MenuLinkRow = ({
  icon,
  badge,
  onPress,
  subtitle,
  title,
  expanded,
}: {
  icon: ReactNode;
  badge?: string;
  title: string;
  subtitle: string;
  onPress: () => void;
  expanded?: boolean;
}) => (
  <button
    type="button"
    onClick={onPress}
    className="block w-full border-b border-border px-4 py-3 text-left transition last:border-b-0 hover:bg-primary/5"
  >
    <XStack minHeight={44} alignItems="center" gap="$sm">
      {icon}
      <YStack flex={1} minWidth={0} gap={2}>
        <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
          {title}
        </AppText>
        <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
          {subtitle}
        </AppText>
      </YStack>
      {badge ? (
        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent px-1.5 text-[10px] font-bold text-accent-foreground">
          {badge}
        </span>
      ) : null}
      {expanded === undefined ? (
        <ChevronRight size={16} className="shrink-0 text-muted-foreground" />
      ) : (
        <ChevronDown
          size={17}
          className={`shrink-0 text-muted-foreground transition ${expanded ? "rotate-180" : ""}`}
        />
      )}
    </XStack>
  </button>
);

export default MyKindred;
